import { Roll } from "assets/icons/Common";
import classNames from "classnames";
import { IconButtonInverted, Row } from "components/primitives";
import { lerp } from "components/utils";
import { times } from "lodash";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";

import { diceConfig } from "./DiceConfig";

const rollIterations = 6;
const rollIterationDelay = [100, 300];
const defaultFaceValue = 6;

export interface InteractiveDiceState {
  diceRolls: number[];
}

interface DieProps {
  Background?: React.ComponentType<any>;
  face: React.ReactNode;
  diceCount: number;
  playAnim: boolean;
}

const Die: React.FC<DieProps> = ({ Background, face, diceCount, playAnim }) => {
  return (
    <DieContainer diceCount={diceCount} playAnim={playAnim}>
      {Background ? (
        <>
          <Background />
          <DiceElement>{face}</DiceElement>
        </>
      ) : (
        face
      )}
    </DieContainer>
  );
};

export const DiceRoller: WidgetFC<InteractiveDiceState> = ({
  state: { diceRolls },
  setState,
  settings,
}) => {
  const { config: configName, count: diceCountString } = settings;
  const config = diceConfig[configName];
  const Background = config.background;
  const diceCount = Number(diceCountString);

  const [isRolling, setIsRolling] = useState(false);
  const [playAnim, setPlayAnim] = useState(false);

  useEffect(() => {
    // Resize array up or down
    if (diceCount > diceRolls.length) {
      const newRolls = [...diceRolls];
      while (newRolls.length < diceCount) {
        newRolls.push(defaultFaceValue - 1);
      }
      setState({ diceRolls: newRolls });
    } else if (diceCount < diceRolls.length) {
      const newRolls = [...diceRolls];
      newRolls.splice(diceCount - 1, diceRolls.length - diceCount);
      setState({ diceRolls: newRolls });
    }
  }, [diceCount, diceRolls, setState]);

  const rollDice = (iteration: number = 0) => {
    const faceCount = config.faces.length;
    setIsRolling(true);

    const newDiceRolls = [...diceRolls];

    // Roll all dice together
    for (let i = 0; i < newDiceRolls.length; i++) {
      newDiceRolls[i] = Math.round(Math.random() * (faceCount - 1));
    }

    setState({ diceRolls: newDiceRolls });

    if (iteration < rollIterations) {
      setTimeout(
        () => rollDice(iteration + 1),
        lerp(rollIterationDelay[0], rollIterationDelay[1], iteration / rollIterations),
      );
    } else {
      setIsRolling(false);
      setPlayAnim(true);
      setTimeout(() => setPlayAnim(false), 1000);
    }
  };

  return (
    <OuterContainer>
      <InnerContainer>
        <DiceContainer>
          {times(diceCount, (num) => (
            <Die
              key={num}
              Background={Background}
              face={
                diceRolls[num] < config.faces.length
                  ? config.faces[diceRolls[num]]
                  : config.faces[defaultFaceValue - 1]
              }
              diceCount={diceCount}
              playAnim={playAnim}
            />
          ))}
        </DiceContainer>
        <GoButton withShadow={false} disabled={isRolling} Icon={Roll} onClick={() => rollDice()} />
      </InnerContainer>
    </OuterContainer>
  );
};

const OuterContainer = styled.div`
  padding: 5cqw;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  container-type: size;

  --animate-duration: 0.5s;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: space-around;
  width: 100%;
`;

const DiceContainer = styled(Row)`
  width: 80%;
  height: 80%;
  justify-content: center;
  align-items: center;
  align-content: center;
  flex-wrap: wrap;
  gap: 3cqw;
`;

const DieContainer = styled.div.attrs<{ playAnim: boolean }>((props) => ({
  className: classNames("animate__animated", props.playAnim && "animate__pulse"),
}))<{
  diceCount: number;
  playAnim: boolean;
}>`
  min-width: 26cqw;
  max-width: ${(props) => (props.diceCount === 1 ? "100%" : "50%")};
  flex-grow: ${(props) => lerp(1, 0.4, (props.diceCount - 1) / 4)}};
  flex-shrink: 1;
  flex-basis: 0;
  position: relative;
`;

const DiceElement = styled(Row)`
  width: 100%;
  height: 100%;
  position: absolute;
  justify-content: center;
  align-items: center;
  p {
    margin: 0;
  }
  top: 0px;
  left: 0px;
`;

const GoButton = styled(IconButtonInverted)`
  width: 20cqw;
  height: 20cqw;
`;
